import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Lang } from "@/lib/i18n";

// Simple in-memory + localStorage cache: key = `${lang}::${text}` → translation
const memCache = new Map<string, string>();

function loadCache(lang: string): Record<string, string> {
  try {
    const raw = localStorage.getItem(`hvi_tr_${lang}`);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}
function saveCache(lang: string, map: Record<string, string>) {
  try {
    localStorage.setItem(`hvi_tr_${lang}`, JSON.stringify(map));
  } catch {}
}

/**
 * Translate an array of texts to `lang`. Returns same-length array.
 * For lang === 'en', returns input as-is. Caches per-text in localStorage.
 */
export function useTranslatedTexts(texts: string[], lang: Lang) {
  const [out, setOut] = useState<string[]>(texts);

  useEffect(() => {
    if (lang === "en") {
      setOut(texts);
      return;
    }
    let cancelled = false;
    const disk = loadCache(lang);
    const mapped = texts.map((t) => {
      if (!t) return "";
      const key = `${lang}::${t}`;
      return memCache.get(key) ?? disk[t] ?? "";
    });
    setOut(mapped.map((m, i) => m || texts[i]));

    const missingIdx: number[] = [];
    const missingTexts: string[] = [];
    texts.forEach((t, i) => {
      if (!t) return;
      const key = `${lang}::${t}`;
      if (!memCache.has(key) && !disk[t]) {
        missingIdx.push(i);
        missingTexts.push(t);
      }
    });
    if (missingTexts.length === 0) return;

    (async () => {
      // Translate in chunks to keep request size sane
      const CHUNK = 20;
      for (let s = 0; s < missingTexts.length; s += CHUNK) {
        const chunk = missingTexts.slice(s, s + CHUNK);
        const idxs = missingIdx.slice(s, s + CHUNK);
        try {
          const { data, error } = await supabase.functions.invoke("translate-batch", {
            body: { texts: chunk, target: lang },
          });
          if (error || cancelled) continue;
          const translations: string[] = (data as any)?.translations ?? chunk;
          translations.forEach((tr, k) => {
            const original = chunk[k];
            disk[original] = tr;
            memCache.set(`${lang}::${original}`, tr);
          });
          saveCache(lang, disk);
          if (cancelled) return;
          setOut((prev) => {
            const next = prev.slice();
            idxs.forEach((origI, k) => {
              if (translations[k]) next[origI] = translations[k];
            });
            return next;
          });
        } catch {
          // ignore; keep originals
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [texts.join("\u0001"), lang]);

  return out;
}
